/**
 * Candidate-facing translations for backend assessment error codes.
 *
 * The backend raises machine-readable codes as
 * ``{ detail: { error: CODE, message: "…" } }`` and ``apiClient`` surfaces
 * ``CODE`` on ``ServiceError.errorCode``. The candidate-assessment guards
 * (TP immutability, participation, test availability, AQ locks, back-nav) are
 * *enforced* by the backend, but the candidate UI used to show only the raw
 * backend sentence — which is often techy ("Test is in 'Closed' status…") or
 * leaks internals (a question id). This module maps the codes a candidate can
 * actually hit to calm, tailored copy, and ``describeError`` is the single
 * helper every candidate error path calls.
 *
 * Override policy: only codes whose raw message is techy / leaks internals are
 * mapped. Codes whose backend message carries a specific the candidate needs
 * (e.g. OPTION_THRESHOLD_NOT_MET → the threshold N; VALIDATION_ERROR → the
 * actual total) are deliberately NOT mapped, so ``describeError`` falls through
 * to the (still human-readable) backend message. This never makes an error
 * LESS informative than before.
 */
import { ServiceError } from '../services/apiClient';

/** errorCode → candidate-facing copy. Kept calm: a guard rejection here is
 *  almost always recoverable (the candidate's saved work is safe), not a crash. */
export const CANDIDATE_ERROR_COPY: Record<string, string> = {
  // Time-Prioritization guards (STB-004 / FR-011) — now enforced AND explained.
  TP_IMMUTABLE_AFTER_AQ:
    'Your time allocation is locked because the questionnaire has already started, so it can no longer be changed.',
  TP_ALREADY_LOCKED:
    'Your time allocation has already been submitted and locked.',

  // Participation / test availability.
  NO_PARTICIPATION:
    "You're not assigned to this assessment. Return to your assessments to see what's available to you.",
  TEST_NOT_ACCESSIBLE:
    "This assessment isn't open right now. Please check back later, or contact your administrator if you think this is a mistake.",
  TEST_NOT_ACTIVE:
    "This assessment isn't open right now. Please check back later, or contact your administrator if you think this is a mistake.",
  ASSESSMENT_COMPLETE:
    'This assessment has already been completed — your responses are saved.',
  ALREADY_COMPLETED:
    'This assessment has already been submitted and can no longer be changed — your responses are saved.',

  // AQ answer guards.
  INVALID_QUESTION:
    "That question isn't part of your assessment anymore. We'll refresh the page to show the right one.",

  // Back navigation (one-step-back feature).
  BACK_NAVIGATION_DISABLED:
    'Going back to a previous question is turned off for this assessment.',
  BACK_ALREADY_USED:
    'Please answer the reopened question before going back again.',
  NOTHING_TO_UNDO:
    "There's no previous answer to go back to yet.",

  // Due date enforcement — test deadline has passed.
  TEST_PAST_DUE_DATE:
    "This assessment's due date has passed and can no longer be accessed. Please contact your administrator if you need an extension.",

  // Generic FSM rejection — the action isn't valid at this point in the flow.
  INVALID_STATE:
    "That action isn't available at this point in your assessment.",
};

/**
 * Resolve a candidate-facing message for any thrown error. Prefers the tailored
 * copy for a known code, then the backend's own ``message`` (still readable, and
 * the place specifics like a threshold or total live), then a generic line.
 */
export function describeError(err: unknown, fallback = 'Please try again.'): string {
  if (
    err instanceof ServiceError &&
    err.errorCode &&
    CANDIDATE_ERROR_COPY[err.errorCode]
  ) {
    return CANDIDATE_ERROR_COPY[err.errorCode];
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return fallback;
}
